<template>
    <div
        ref="root"
        class="table-header-search"
        @click.stop
    >
        <v-btn
            v-if="showIcon"
            size="small"
            icon
            variant="text"
            density="compact"
            aria-label="Search"
            @click="openSearch"
        >
            <v-icon>mdi-magnify</v-icon>
        </v-btn>
        <Transition name="search-field" @after-leave="onAfterLeave">
            <input
                v-if="open"
                ref="input"
                class="table-header-search__input"
                type="search"
                :value="modelValue"
                :placeholder="placeholder"
                @input="onInput"
                @keydown.esc="closeSearch"
            />
        </Transition>
    </div>
</template>

<script>
/**
 * Header spyglass that overlays a plain input (no layout shift).
 * Parent tables own filtering.
 */
export default {
    name: "TableHeaderSearch",
    props: {
        modelValue: { type: String, default: "" },
        open: { type: Boolean, default: false },
        placeholder: { type: String, default: "Search" },
    },
    emits: ["update:modelValue", "update:open"],
    data() {
        return {
            showIcon: !this.open,
        };
    },
    watch: {
        open(isOpen) {
            if (isOpen) {
                this.showIcon = false;
                this.bindOutsideListener();
                this.$nextTick(() => this.$refs.input?.focus());
            } else {
                this.unbindOutsideListener();
            }
        },
    },
    beforeUnmount() {
        this.unbindOutsideListener();
    },
    methods: {
        onInput(event) {
            this.$emit("update:modelValue", event.target?.value ?? "");
        },
        openSearch() {
            this.showIcon = false;
            this.$emit("update:open", true);
        },
        closeSearch() {
            this.$emit("update:modelValue", "");
            this.$emit("update:open", false);
        },
        onAfterLeave() {
            if (!this.open) {
                this.showIcon = true;
            }
        },
        onDocumentPointerDown(event) {
            if (!this.open) return;
            const root = this.$refs.root;
            if (!root || root.contains(event.target)) return;

            // Clicks on filtered rows are inside the table — keep search open
            // so the list does not reset before the row action runs.
            const table = root.closest(".v-data-table, .v-table");
            if (table && table.contains(event.target)) return;

            this.closeSearch();
        },
        bindOutsideListener() {
            document.addEventListener(
                "pointerdown",
                this.onDocumentPointerDown,
                true
            );
        },
        unbindOutsideListener() {
            document.removeEventListener(
                "pointerdown",
                this.onDocumentPointerDown,
                true
            );
        },
    },
};
</script>

<style scoped>
.table-header-search {
    position: relative;
    width: 2rem;
    height: 2rem;
    margin-left: 0.25rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.table-header-search__input {
    position: absolute;
    left: 0;
    top: 50%;
    z-index: 3;
    width: 10rem;
    height: 1.75rem;
    margin: 0;
    padding: 0 0.25rem;
    border: none;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    background: rgb(var(--v-theme-surface));
    color: inherit;
    font: inherit;
    outline: none;
    transform: translateY(-50%);
    transform-origin: left center;
}

.search-field-enter-active,
.search-field-leave-active {
    transition: width 0.2s ease, opacity 0.2s ease;
}

.search-field-enter-from,
.search-field-leave-to {
    width: 2rem;
    opacity: 0.4;
}

.search-field-enter-to,
.search-field-leave-from {
    width: 10rem;
    opacity: 1;
}
</style>
